import {
  Box,
  Button,
  Stack,
  Typography,
  Card,
  useTheme,
  FormControlLabel,
  Switch,
  IconButton,
  Badge,
  Alert,
  CircularProgress,
} from '@mui/material'
import CheckCircleOutlineRoundedIcon from '@mui/icons-material/CheckCircleOutlineRounded'
import CloseIcon from '@mui/icons-material/Close'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import { useState, useEffect } from 'react'
import UploadDialog from '../UploadDialog'
import TimePointSubmissionStatusChip from '../TimePointSubmissionStatusChip'
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined'
import { EsignContextType, TimepointContainerEsignContext } from '@common/config/api/client'
import ESignDialog from '@features/subject/components/eSignDialog'
import FormRendererDialog from '../FormRendererDialog'
import { useSubmitTimePointEForm } from '@features/subject/hooks/useSubmitTimePointEForm'
import { useUpdateContainerSubmission } from '@features/subject/hooks/useUpdateContainerSubmission.ts'
import { useParams } from 'react-router-dom'
import NoFilesBlock from '@features/subject/components/NoFilesBlock'
import ExpandMoreIcon from '@mui/icons-material/ExpandMore'
import { styled } from '@mui/material/styles'
import { getAdherenceCommentText, getOverrideCommentText } from '@common/utils/parseContainerComments'
import Collapse from '@mui/material/Collapse'
import dayjs from 'dayjs'
import { TimePointConfigurationProps } from '@features/subject/subject.types'
import { SPACING } from '@common/theme/spacing'
import React from 'react'
import ExamDetailPopupMenuList from './ExamDetailPopupMenuList'
import { formatFileSize } from '@features/subject/utils.ts'
import { launchDicomViewerForSiteMultipleContainer } from '@common/utils/ohifUtils'
import ModalityViewerSelector from './ModalityViewerSelector'
import { useExtendedAuth } from '@auth/hooks/useExtendedAuth'
import { useSignPayload } from '@features/subject/hooks/useSignPayload.tsx'
import { useRestoreSigningProcess } from '@auth/hooks/useRestoreSigningProcess'
import AdditionalCommentSection from '@features/subject/components/AdditionalCommentSection'

function NotPerformedComments({ comments }: { comments?: string }) {
  const overrideComment = getOverrideCommentText(comments)
  const adherenceComment = getAdherenceCommentText(comments, true)
  return (
    <>
      {overrideComment ? (
        <>
          <Typography variant="subtitle2" color="text.secondary">
            Override comment
          </Typography>
          <Typography mb={adherenceComment ? SPACING.spacingLg : 0}>{overrideComment}</Typography>
        </>
      ) : null}
      {adherenceComment ? (
        <>
          <Typography variant="subtitle2" color="text.secondary">
            Adherence comment
          </Typography>
          <Typography>{adherenceComment}</Typography>
        </>
      ) : null}
    </>
  )
}

const ExpandMore = styled('div')(({ theme }) => ({
  display: 'flex',
  cursor: 'pointer',
  transition: theme.transitions.create('transform', {
    duration: theme.transitions.duration.shortest,
  }),
}))

export default function SubjectTimePointConfiguration({
  name,
  status,
  timePointFormId,
  eFormFields,
  eFormValues,
  subjectData,
  containerParam,
  onContainerSubmissionComplete,
}: TimePointConfigurationProps) {
  const theme = useTheme()
  const { studyId, subjectId, timepointSubmissionId } = useParams<{
    studyId: string
    subjectId: string
    timepointSubmissionId: string
  }>()
  const auth = useExtendedAuth()

  const { mutateAsync: submitTimePointEForm } = useSubmitTimePointEForm()
  const { mutateAsync: submitContainerEForm } = useUpdateContainerSubmission()
  const timepointSignMutation = useSignPayload()
  const [filesUploadDialogVisible, setFilesUploadDialogVisible] = useState(false)
  const [showProgressSaved, setShowProgressSaved] = React.useState(false)

  const [eSignDialogState, setESignDialogState] = useState<{
    payload: Record<string, unknown> & TimepointContainerEsignContext
    open: boolean
  }>({
    payload: {
      user_role_association_id: '',
      type: 'timepoint_container_submission' as EsignContextType,
      container_submissions: [],
    },
    open: false,
  })

  const [containerExpanded, setContainerExpanded] = useState(() =>
    containerParam.reduce<Record<string, boolean>>((acc, item) => {
      acc[item.containerSubmissionId] = Boolean(
        (item.examMetadata && item.examMetadata.length > 0) || (item.comments && item.noFileSubmissionReason),
      )
      return acc
    }, {}),
  )

  const [containerCommentSection, setContainerCommentSection] = useState(() =>
    containerParam.reduce<Record<string, boolean>>((acc, item) => {
      acc[item.containerSubmissionId] = Boolean(item.comments && item.noFileSubmissionReason)
      return acc
    }, {}),
  )

  const shouldShowExpandBtn = (item: (typeof containerParam)[number]) =>
    Boolean(
      containerCommentSection[item.containerSubmissionId] ||
        containerExpanded[item.containerSubmissionId] ||
        item.noFileSubmissionReason ||
        item.comments ||
        item.additionalComments ||
        item.examMetadata ||
        item.status !== 'COMPLETE',
    )

  useEffect(() => {
    const newExpandedState = { ...containerExpanded }
    let stateChanged = false

    containerParam.forEach((item) => {
      const hasExamMetadata = !!(item.examMetadata && item.examMetadata.length > 0)

      if (!(item.containerSubmissionId in newExpandedState)) {
        newExpandedState[item.containerSubmissionId] = hasExamMetadata
        stateChanged = true
      }

      if (hasExamMetadata && !newExpandedState[item.containerSubmissionId]) {
        newExpandedState[item.containerSubmissionId] = true
        stateChanged = true
      }
    })

    if (stateChanged) {
      setContainerExpanded(newExpandedState)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [containerParam])

  const handleContainerExpandToggle = (containerId: string, forceState?: boolean) => {
    setContainerExpanded({
      ...containerExpanded,
      [containerId]: forceState !== undefined ? forceState : !containerExpanded[containerId],
    })
  }

  const handleOpenViewerForModalities = (selectedModalities: string[]) => {
    if (!studyId || !subjectId || !timepointSubmissionId || !auth?.user?.access_token) {
      console.error('Missing parameters for launching DICOM viewer')
      return
    }

    const viewableStatuses = ['COMPLETE', 'LOCKED', 'FILE_UPLOAD_COMPLETED']
    const containerSubmissionIds = containerParam
      .filter(
        (c) =>
          (c.type === 'DICOM' || (c.type === 'NON_DICOM' && c.convertToDicom)) &&
          viewableStatuses.includes(c.status ?? '') &&
          !c.noFilesSubmitted &&
          c.examMetadata &&
          c.examMetadata.length > 0 &&
          selectedModalities.includes(c.modality),
      )
      .map((c) => c.containerSubmissionId)

    if (containerSubmissionIds.length === 0) {
      console.warn('No viewable containers found for selected modalities')
      return
    }

    launchDicomViewerForSiteMultipleContainer(studyId, subjectId, timepointSubmissionId, containerSubmissionIds)
  }

  const handleContainerReasonToggle = (containerId: string, forceState?: boolean) => {
    setContainerCommentSection({
      ...containerCommentSection,
      [containerId]: forceState !== undefined ? forceState : !containerCommentSection[containerId],
    })
    if (!containerExpanded[containerId]) {
      handleContainerExpandToggle(containerId, true)
    }
  }

  const [eFormDialogState, setEFormUploadDialogState] = useState<{
    visible: boolean
    eFormFields?: string | null
    eFormValues?: string | null
    source: 'TIMEPOINT' | 'CONTAINER'
    containerSubmissionId?: string | null
  }>({
    visible: false,
    eFormFields: null,
    eFormValues: null,
    source: 'TIMEPOINT',
  })

  const handleSubmitEForm = async (data: Record<string, string | number | boolean>) => {
    if (eFormDialogState.source === 'TIMEPOINT') {
      await submitTimePointEForm({
        body: {
          form_submission: data,
        },
        path: {
          study_id: studyId!,
          subject_id: subjectId!,
          timepoint_submission_id: timepointSubmissionId!,
        },
      })
    } else {
      await submitContainerEForm({
        body: [
          {
            form_submission: data,
            container_submission_id: eFormDialogState.containerSubmissionId!,
          },
        ],
        path: {
          study_id: studyId!,
          subject_id: subjectId!,
          timepoint_submission_id: timepointSubmissionId!,
        },
      })
    }

    setEFormUploadDialogState({ visible: false, source: 'TIMEPOINT', eFormFields: null })

    if (onContainerSubmissionComplete) {
      onContainerSubmissionComplete()
    }
  }

  const handleESignClick = () => {
    setESignDialogState({
      payload: {
        type: 'timepoint_container_submission',
        user_role_association_id: '9f6239c5-a75e-4cc4-a632-264ec30d61f3',
        container_submissions: containerParam.map((x) => ({ container_submission_id: x.containerSubmissionId })),
      },
      open: true,
    })
  }

  const onSave = () => {
    setShowProgressSaved(true)
    if (onContainerSubmissionComplete) {
      onContainerSubmissionComplete()
    }
  }

  const onShowProgressSaved = () => {
    setShowProgressSaved(false)
  }

  const { isSigningInProgress } = useRestoreSigningProcess(timepointSignMutation, {
    successMessage: 'Visit submission signed successfully',
    errorMessage: 'Failed to complete submission signing',
    contextType: 'timepoint_container_submission',
  })

  const isSubmitButtonDisabled = () => {
    const isTimepointEFormComplete = !timePointFormId || !!eFormValues

    const areAllContainersComplete = containerParam.every((container) => {
      if (container.isOptional) {
        return true
      }

      const hasValidStatus = ['COMPLETE', 'LOCKED', 'FILE_UPLOAD_COMPLETED'].includes(container.status!)

      const isContainerEFormComplete = !container.eFormFields || !!container.eFormValues

      const hasNoFileSubmission = container.noFilesSubmitted && container.noFileSubmissionReason

      return (hasValidStatus || hasNoFileSubmission) && isContainerEFormComplete
    })

    return !isTimepointEFormComplete || !areAllContainersComplete
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between">
        <Box display="flex" alignItems="center" gap={2}>
          <Typography variant="h5">{name}</Typography>
          <TimePointSubmissionStatusChip status={status} />
        </Box>
        {status !== 'LOCKED' && status !== 'COMPLETE' ? (
          <Button
            variant="contained"
            size="large"
            color="primary"
            startIcon={isSigningInProgress ? <CircularProgress size={20} color="inherit" /> : null}
            onClick={handleESignClick}
            disabled={isSubmitButtonDisabled()}
          >
            {isSigningInProgress ? 'Signing...' : 'Submit visit'}
          </Button>
        ) : null}
      </Box>
      <Stack gap={SPACING.spacingLg} mt={SPACING.spacingXl}>
        {timePointFormId ? (
          <Card sx={{ p: SPACING.spacingXl, borderRadius: SPACING.borderRadiusXxl }} elevation={1}>
            <Box
              display="flex"
              alignItems="center"
              justifyContent="space-between"
              bgcolor="paper.main"
              p={SPACING.minSpacing}
              borderRadius={SPACING.borderRadiusXl}
            >
              <Box display="flex" alignItems="flex-start">
                <CheckCircleOutlineRoundedIcon
                  sx={{ color: eFormValues ? 'success.main' : 'text.disabled', mt: SPACING.spacingXs }}
                />
                <Box ml={SPACING.minSpacing}>
                  <Typography variant="h6">Clinical information form</Typography>
                  <Typography variant="body1" color="text.secondary">
                    {eFormValues ? 'Form completed' : 'Fill out eForm'}
                  </Typography>
                </Box>
              </Box>

              {status === 'LOCKED' || status === 'COMPLETE' ? (
                eFormValues ? (
                  <Button
                    variant="outlined"
                    size="medium"
                    color="secondary"
                    onClick={() =>
                      setEFormUploadDialogState({
                        visible: true,
                        eFormFields,
                        eFormValues,
                        source: 'TIMEPOINT',
                        containerSubmissionId: null,
                      })
                    }
                  >
                    View form
                  </Button>
                ) : (
                  <Typography variant="body2" color="text.secondary">
                    Not submitted
                  </Typography>
                )
              ) : (
                <>
                  {eFormValues ? (
                    <IconButton
                      onClick={() =>
                        setEFormUploadDialogState({
                          visible: true,
                          eFormFields,
                          eFormValues,
                          source: 'TIMEPOINT',
                          containerSubmissionId: null,
                        })
                      }
                    >
                      <EditRoundedIcon />
                    </IconButton>
                  ) : (
                    <Button
                      variant="contained"
                      size="medium"
                      color="secondary"
                      onClick={() =>
                        setEFormUploadDialogState({
                          visible: true,
                          eFormFields,
                          source: 'TIMEPOINT',
                          containerSubmissionId: null,
                        })
                      }
                    >
                      Fill out form
                    </Button>
                  )}
                </>
              )}
            </Box>
          </Card>
        ) : null}
        <Card sx={{ p: SPACING.spacingXl, borderRadius: SPACING.borderRadiusXxl }} elevation={1}>
          <Box>
            <Box display="flex" alignItems="center" gap={SPACING.spacingLg} flexWrap="wrap">
              <CheckCircleOutlineRoundedIcon
                sx={{
                  color: status !== 'LOCKED' && status !== 'COMPLETE' ? 'text.disabled' : 'success.main',
                  flexShrink: 0,
                }}
              />
              <Typography variant="h6" noWrap>
                Exam results
              </Typography>
              <Box sx={{ ml: 'auto' }}>
                {(status === 'LOCKED' || status === 'COMPLETE') && (
                  <ModalityViewerSelector
                    modalities={Array.from(new Set(containerParam.flatMap((c) => c.modalityAllowed)))}
                    modalitiesWithData={Array.from(
                      new Set(
                        containerParam
                          .filter(
                            (c) =>
                              (c.type === 'DICOM' || (c.type === 'NON_DICOM' && c.convertToDicom)) &&
                              ['COMPLETE', 'LOCKED', 'FILE_UPLOAD_COMPLETED'].includes(c.status ?? '') &&
                              !c.noFilesSubmitted &&
                              c.examMetadata &&
                              c.examMetadata.length > 0,
                          )
                          .flatMap((c) => c.modality),
                      ),
                    )}
                    onOpenViewer={handleOpenViewerForModalities}
                    disabled={!studyId || !subjectId || !timepointSubmissionId || !auth?.user?.access_token}
                  />
                )}
              </Box>
            </Box>
            {status !== 'LOCKED' && status !== 'COMPLETE' && (
              <Box display="flex" alignItems="center" justifyContent="space-between" mt={SPACING.spacingXs}>
                <Typography variant="body1" color="text.secondary">
                  Upload files for completed exams. Please indicate if an exam wasn't performed.
                </Typography>
                <Button
                  variant="contained"
                  size="medium"
                  color="primary"
                  onClick={() => setFilesUploadDialogVisible(true)}
                >
                  Upload files
                </Button>
              </Box>
            )}
          </Box>

          {showProgressSaved && (
            <Alert
              severity="info"
              icon={false}
              sx={{ mt: 2, bgcolor: 'background.paper' }}
              action={
                <IconButton
                  aria-label="close"
                  sx={{ color: 'text.primary' }}
                  size="small"
                  onClick={onShowProgressSaved}
                >
                  <CloseIcon fontSize="inherit" />
                </IconButton>
              }
            >
              <Typography variant="body1" sx={{ color: 'text.primary' }} fontWeight="700" component={'p'}>
                Your progress is saved
              </Typography>
              <Typography variant="body1" sx={{ color: 'text.primary' }} component={'p'}>
                You can continue uploading later starting at Step 1
              </Typography>
            </Alert>
          )}
          <Stack gap={SPACING.spacingLg} mt={SPACING.spacingXxl}>
            {containerParam.map((item) => {
              return (
                <Box
                  key={item.containerSubmissionId || item.name}
                  p={SPACING.spacingLg}
                  borderRadius={SPACING.borderRadiusXl}
                  border="1px solid"
                  borderColor={theme.palette.divider}
                >
                  <Box display="flex" alignItems="center" justifyContent="space-between" flexDirection="row">
                    <Box>
                      <Box display="flex" alignItems="center">
                        {(item.status === 'COMPLETE' ||
                          item.status === 'LOCKED' ||
                          item.status === 'FILE_UPLOAD_COMPLETED' ||
                          (item.examMetadata && item.examMetadata.length > 0)) && (
                          <CheckCircleOutlineRoundedIcon sx={{ color: 'success.main', mr: SPACING.spacingSm }} />
                        )}
                        <Typography variant="h6">{item.name}</Typography>
                      </Box>
                      <Box display="flex">
                        <Typography variant="body2" color="text.secondary">
                          {item.type === 'DICOM' ? 'DICOM files' : 'Non-DICOM files'}
                        </Typography>

                        {item.isOptional ? (
                          <>
                            <Typography mx={SPACING.minSpacing} variant="body2" color="text.secondary">
                              •
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              Optional
                            </Typography>
                          </>
                        ) : null}
                      </Box>
                    </Box>

                    {/* Group Button and Expand/Collapse toggle to the right */}
                    <Box display="flex" alignItems="center" gap={SPACING.minSpacing}>
                      {/* Only show toggle if status is NOT_STARTED and not optional */}
                      {item.status === 'NOT_STARTED' && !item.isOptional ? (
                        <FormControlLabel
                          // Remove margin/padding from label to align better
                          sx={{ m: 0, '& .MuiFormControlLabel-label': { display: 'none' } }}
                          checked={item.noFilesSubmitted || containerCommentSection[item.containerSubmissionId]}
                          control={<Switch color="primary" />}
                          label="Not performed for this visit" // Label is visually hidden but kept for accessibility
                          onChange={(_, checked) => handleContainerReasonToggle(item.containerSubmissionId, checked)}
                          labelPlacement="start"
                        />
                      ) : null}

                      {shouldShowExpandBtn(item) ? (
                        <ExpandMore
                          data-testid={`expand-container-button-${item.containerSubmissionId}`}
                          onClick={() => handleContainerExpandToggle(item.containerSubmissionId)}
                          sx={{
                            transform: containerExpanded[item.containerSubmissionId]
                              ? 'rotate(180deg)'
                              : 'rotate(0deg)',
                          }}
                        >
                          <ExpandMoreIcon />
                        </ExpandMore>
                      ) : null}
                    </Box>
                  </Box>

                  <Collapse in={containerExpanded[item.containerSubmissionId]} timeout="auto" unmountOnExit>
                    {/* Only show NoFilesBlock if status is NOT_STARTED and not optional */}
                    {item.status === 'NOT_STARTED' &&
                    !item.isOptional &&
                    containerCommentSection[item.containerSubmissionId] ? (
                      <NoFilesBlock
                        containerSubmissionId={item.containerSubmissionId}
                        onSign={(containerSubmissionId) => handleContainerExpandToggle(containerSubmissionId)}
                      />
                    ) : null}

                    {/* Not-performed reason + override/adherence comments (structured JSON or legacy plain text) */}
                    {!item.isOptional && item.status !== 'NOT_STARTED' && item.noFileSubmissionReason ? (
                      <Box mt={SPACING.spacingLg} bgcolor="paper.main" sx={{ padding: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Reason for Override
                        </Typography>
                        <Typography mb={SPACING.spacingLg}>{item.noFileSubmissionReason}</Typography>
                        <NotPerformedComments comments={item.comments} />
                      </Box>
                    ) : null}

                    {/* Always show exam metadata if available */}
                    {item.examMetadata?.length ? (
                      <Box my={SPACING.spacingLg}>
                        <Box display="flex" alignItems="center" mb={SPACING.minSpacing}>
                          <Typography variant="subtitle2" color="text.secondary" mr={SPACING.timePointSpacing}>
                            Exams
                          </Typography>
                          <Badge variant="standard" color="secondary" badgeContent={item.examMetadata?.length} />
                        </Box>
                        <Box borderRadius={SPACING.borderRadiusXl} overflow="hidden">
                          {item.examMetadata?.map((subItem, index) => (
                            <Box
                              key={index}
                              px={SPACING.spacingLg}
                              py={SPACING.minSpacing}
                              bgcolor="paper.main"
                              borderBottom={
                                index !== (item.examMetadata?.length || 0) - 1
                                  ? `1px solid ${theme.palette.divider}`
                                  : undefined
                              }
                              display="flex"
                              gap={SPACING.spacingLg}
                              alignItems="center"
                            >
                              {item.type === 'DICOM' || (item.type === 'NON_DICOM' && item.convertToDicom) ? (
                                <>
                                  <Box flex={5}>
                                    <Box display="flex" mb={SPACING.spacingSm} alignItems="center">
                                      <Typography variant="subtitle2" color="text.secondary" mr={SPACING.spacingMd}>
                                        Study description
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2">{subItem.study_description}</Typography>
                                  </Box>
                                  <Box flex={2}>
                                    <Typography variant="subtitle2" color="text.secondary" mb={SPACING.spacingXs}>
                                      Acquisition date
                                    </Typography>
                                    <Typography variant="body2">
                                      {dayjs(subItem.acquisition_date, 'YYYYMMDD').format('DD/MM/YYYY')}
                                    </Typography>
                                  </Box>
                                  <Box flex={2}>
                                    <Typography variant="subtitle2" color="text.secondary" mb={SPACING.spacingXs}>
                                      № of series
                                    </Typography>
                                    <Typography variant="body2">{subItem.series.length}</Typography>
                                  </Box>
                                  <Box flex={2}>
                                    <Typography variant="subtitle2" color="text.secondary" mb={SPACING.spacingXs}>
                                      Criteria adherence
                                    </Typography>
                                    <Typography variant="body2">{item.criteriaCheck}</Typography>
                                  </Box>
                                  <Box>
                                    <ExamDetailPopupMenuList
                                      subject_pseudo_id={subjectData?.pseudo_id}
                                      container_submission_id={item.containerSubmissionId}
                                      containers={containerParam}
                                      exam={subItem}
                                      timepointSubmissionStatus={status}
                                    />
                                  </Box>
                                </>
                              ) : (
                                <>
                                  <Box flex={6}>
                                    <Box display="flex" mb={SPACING.spacingSm} alignItems="center">
                                      <Typography variant="subtitle2" color="text.secondary" mr={SPACING.spacingMd}>
                                        File name
                                      </Typography>
                                    </Box>
                                    <Typography variant="body2">{subItem.file_name}</Typography>
                                  </Box>
                                  <Box flex={2}>
                                    <Typography variant="subtitle2" color="text.secondary" mb={SPACING.spacingXs}>
                                      Format
                                    </Typography>
                                    <Typography variant="body2">{subItem.file_extension?.toUpperCase()}</Typography>
                                  </Box>
                                  <Box flex={2}>
                                    <Typography variant="subtitle2" color="text.secondary" mb={SPACING.spacingXs}>
                                      Size
                                    </Typography>
                                    <Typography variant="body2">
                                      {formatFileSize(parseInt(subItem.file_size || '0'))}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <ExamDetailPopupMenuList
                                      subject_pseudo_id={subjectData?.pseudo_id}
                                      container_submission_id={item.containerSubmissionId}
                                      containers={containerParam}
                                      exam={subItem}
                                      timepointSubmissionStatus={status}
                                      isDicom={false}
                                    />
                                  </Box>
                                </>
                              )}
                            </Box>
                          ))}
                        </Box>
                      </Box>
                    ) : null}

                    {status !== 'COMPLETE' && status !== 'LOCKED' ? (
                      <AdditionalCommentSection
                        containerSubmissionId={item.containerSubmissionId}
                        additionalComment={item?.additionalComments || ''}
                      />
                    ) : null}

                    {(status == 'COMPLETE' || status == 'LOCKED') && item.additionalComments ? (
                      <Box mt={SPACING.spacingLg} bgcolor="paper.main" sx={{ padding: 2 }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Additional Comment
                        </Typography>
                        <Typography>{item.additionalComments}</Typography>
                      </Box>
                    ) : null}
                  </Collapse>
                  {item.eFormFields ? (
                    <Box mt={SPACING.spacingLg}>
                      <Typography variant="subtitle2" color="text.secondary" mb={SPACING.minSpacing}>
                        Electronic form
                      </Typography>
                      <Box
                        display="flex"
                        alignItems="center"
                        justifyContent="space-between"
                        p={SPACING.minSpacing}
                        bgcolor="paper.main"
                        borderRadius={SPACING.borderRadiusXl}
                      >
                        <Box display="flex" alignItems="center">
                          <DescriptionOutlinedIcon
                            sx={{
                              color: item.eFormValues ? 'success.main' : 'text.disabled',
                            }}
                          />
                          <Typography variant="subtitle2" color="text.primary" ml={SPACING.minSpacing}>
                            eForm
                          </Typography>
                        </Box>

                        {status === 'LOCKED' || status === 'COMPLETE' ? (
                          item.eFormValues ? (
                            <Button
                              variant="outlined"
                              size="small"
                              color="secondary"
                              onClick={() =>
                                setEFormUploadDialogState({
                                  visible: true,
                                  eFormFields: item.eFormFields,
                                  eFormValues: item.eFormValues,
                                  source: 'CONTAINER',
                                  containerSubmissionId: item.containerSubmissionId,
                                })
                              }
                            >
                              View form
                            </Button>
                          ) : (
                            <Typography variant="body2" color="text.secondary">
                              Not submitted
                            </Typography>
                          )
                        ) : (
                          <>
                            {item.eFormValues ? (
                              <IconButton
                                size="small"
                                aria-label="Edit container eForm"
                                onClick={() =>
                                  setEFormUploadDialogState({
                                    visible: true,
                                    eFormFields: item.eFormFields,
                                    eFormValues: item.eFormValues,
                                    source: 'CONTAINER',
                                    containerSubmissionId: item.containerSubmissionId,
                                  })
                                }
                              >
                                <EditRoundedIcon fontSize="small" />
                              </IconButton>
                            ) : (
                              <Button
                                variant="contained"
                                size="small"
                                color="secondary"
                                onClick={() =>
                                  setEFormUploadDialogState({
                                    visible: true,
                                    eFormFields: item.eFormFields,
                                    source: 'CONTAINER',
                                    containerSubmissionId: item.containerSubmissionId,
                                  })
                                }
                              >
                                Fill out form
                              </Button>
                            )}
                          </>
                        )}
                      </Box>
                    </Box>
                  ) : null}
                </Box>
              )
            })}
          </Stack>
        </Card>

        {/* Debug info panel - only visible in development mode */}
        {import.meta.env.DEV && (
          <Card sx={{ p: SPACING.spacingXl, borderRadius: SPACING.borderRadiusXxl, mt: 2 }} elevation={1}>
            <Typography variant="h6" color="error">
              Debug Info
            </Typography>
            <Button
              variant="contained"
              color="warning"
              size="small"
              onClick={() => {
                if (onContainerSubmissionComplete) {
                  console.log('Manually refreshing data...')
                  onContainerSubmissionComplete()
                }
              }}
              sx={{ mb: 2 }}
            >
              Force Refresh Data
            </Button>
            {containerParam.map((item, index) => (
              <Box key={index} sx={{ mb: 2, p: 2, border: '1px dashed grey' }}>
                <Typography variant="subtitle1">
                  {item.name} ({item.type})
                </Typography>
                <Typography variant="body2">Status: {item.status}</Typography>
                <Typography variant="body2">
                  Expanded: {String(containerExpanded[item.containerSubmissionId])}
                </Typography>
                <Typography variant="body2">Container ID: {item.containerSubmissionId}</Typography>
                <Typography variant="body2">
                  Has exam_metadata: {String(!!(item.examMetadata && item.examMetadata.length > 0))}
                </Typography>
                <Typography variant="body2">Exam metadata count: {item.examMetadata?.length || 0}</Typography>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleContainerExpandToggle(item.containerSubmissionId, true)}
                  sx={{ mr: 1, mt: 1 }}
                >
                  Expand
                </Button>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => handleContainerExpandToggle(item.containerSubmissionId, false)}
                >
                  Collapse
                </Button>
              </Box>
            ))}
          </Card>
        )}

        {filesUploadDialogVisible && studyId && subjectId && timepointSubmissionId && (
          <UploadDialog
            timepointName={name}
            containerParam={containerParam}
            subjectPseudoId={subjectData?.pseudo_id}
            studyId={studyId}
            subjectId={subjectId}
            timepointSubmissionId={timepointSubmissionId}
            onSave={onSave}
            onClose={() => setFilesUploadDialogVisible(false)}
          />
        )}
        {eSignDialogState.open && (
          <ESignDialog
            payload={eSignDialogState.payload}
            onClose={() => {
              setESignDialogState({ ...eSignDialogState, open: false })
            }}
          />
        )}
        {eFormDialogState.visible && (
          <FormRendererDialog
            header={
              status === 'LOCKED' || status === 'COMPLETE'
                ? `View ${eFormDialogState.source === 'TIMEPOINT' ? 'Clinical Information' : 'Container'} Form`
                : `Fill ${eFormDialogState.source === 'TIMEPOINT' ? 'Clinical Information' : 'Container'} Form`
            }
            onClose={() => setEFormUploadDialogState({ visible: false, source: 'TIMEPOINT', eFormFields: null })}
            fields={eFormDialogState.eFormFields}
            values={eFormDialogState.eFormValues}
            readOnly={status === 'LOCKED' || status === 'COMPLETE'}
            onSubmit={handleSubmitEForm}
          />
        )}
      </Stack>
    </Box>
  )
}
